import * as React from 'react';
import { FC, createContext, useContext, useEffect, useRef } from 'react';

import usePosVendor from 'hooks/menu/use-pos-vendor';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import useMediaQuery, { MediaQuery } from 'hooks/use-media-query';
import { LaunchDarklyFlag, useFlag, useLDContext } from 'state/launchdarkly';
import { useLoyaltyContext } from 'state/loyalty';
import { removeAppliedRewardsInStorage } from 'state/loyalty/hooks/use-loyalty-rewards';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';

import { useInRestaurantRedemptionCart } from './hooks/use-in-restaurant-redemption-cart';
import { ShortCodeState, useShortCode } from './hooks/use-short-code';
import { ShortCodePollingStatus, useShortCodePollStatus } from './hooks/use-short-code-poll-status';
import { IInRestaurantRedemptionContext } from './types';
import { generateOrderLineItems } from './utils';

export const InRestaurantRedemptionContext = createContext<IInRestaurantRedemptionContext>(
  {} as IInRestaurantRedemptionContext
);
export const useInRestaurantRedemptionContext = () => useContext(InRestaurantRedemptionContext);

export const InRestaurantRedemptionProvider: FC = ({ children }) => {
  const { vendor: posVendor } = usePosVendor();
  const { loyaltyUser, loyaltyLoading, resetLoyaltyRewardState } = useLoyaltyContext();
  const { noStoreSelected, store } = useStoreContext();
  const { isDelivery } = useServiceModeContext();
  const isMobile = useMediaQuery(MediaQuery.Mobile);
  const previousLoyaltyUserIdRef = useRef<string>();
  const { ldUserIsAuthenticated } = useLDContext();

  const enableInRestaurantRedemption = Boolean(
    useFlag(LaunchDarklyFlag.ENABLE_IN_RESTAURANT_LOYALTY)
  );
  const inRestaurantLoyaltyEnabledAtRestaurant = Boolean(
    useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_AT_THIS_RESTAURANT)
  );
  const inRestaurantRedemptionStatusPollIntervalMs = useFlag(
    LaunchDarklyFlag.LOYALTY_IN_RESTAURANT_STATUS_POLL_INTERVAL_MS
  );

  const {
    inRestaurantRedemptionCart,
    addInRestaurantRedemptionEntry,
    removeInRestaurantRedemptionEntry,
    updateInRestaurantRedemptionEntryQuantity,
    findInRestaurantCartEntryByIncentive,
    resetInRestaurantRedemptionCart,
    isInRestaurantRedemptionCartEmpty,
    existEntryTypeInRestaurantRedemptionCart,
    clearInRestaurantRedemptionAllRewards,
  } = useInRestaurantRedemptionCart();
  const {
    resetShortCode,
    getNewShortCode,
    shortCodeLoading,
    shortCodeState,
    setShortCodeClaimed,
    shortCode,
  } = useShortCode();

  const skipPolling: boolean =
    !inRestaurantRedemptionStatusPollIntervalMs ||
    shortCodeLoading ||
    shortCodeState !== ShortCodeState.Pending;

  const { pollingShortCodeStatus, resetPollingStatus } = useShortCodePollStatus({
    skipPolling,
    shortCode,
    pollInterval: inRestaurantRedemptionStatusPollIntervalMs,
  });

  const loyaltyUserId = loyaltyUser?.id;
  const inRestaurantRedemptionEnabled =
    isMobile && enableInRestaurantRedemption && !isDelivery && (!!loyaltyUserId || loyaltyLoading);
  const storeNumber = store?.number;

  const resetInRestaurantRedemption = () => {
    resetPollingStatus();
    resetShortCode();
    resetInRestaurantRedemptionCart();

    // reset loyalty state
    removeAppliedRewardsInStorage();
    resetLoyaltyRewardState();
  };

  const generateShortCode = () => {
    if (loyaltyUserId && !noStoreSelected) {
      getNewShortCode({
        inRestaurantOrder: generateOrderLineItems(inRestaurantRedemptionCart, posVendor),
        loyaltyId: loyaltyUserId,
        restaurantId: storeNumber,
      });
    }
  };

  useEffectOnUpdates(() => {
    resetShortCode();
    // generate a new short code on every cart update
    generateShortCode();
  }, [inRestaurantRedemptionCart]);

  useEffect(() => {
    if (pollingShortCodeStatus === ShortCodePollingStatus.Confirmed) {
      setShortCodeClaimed();
    }
  }, [pollingShortCodeStatus, setShortCodeClaimed]);

  useEffect(() => {
    previousLoyaltyUserIdRef.current = loyaltyUserId;
  }, [loyaltyUserId]);

  useEffect(() => {
    // clean cart if the user changes or the feature isn't enabled
    if (
      !loyaltyLoading &&
      !isInRestaurantRedemptionCartEmpty &&
      (previousLoyaltyUserIdRef.current !== loyaltyUserId ||
        !inRestaurantRedemptionEnabled ||
        (!inRestaurantLoyaltyEnabledAtRestaurant && ldUserIsAuthenticated))
    ) {
      resetInRestaurantRedemption();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loyaltyLoading,
    loyaltyUserId,
    inRestaurantRedemptionEnabled,
    inRestaurantLoyaltyEnabledAtRestaurant,
  ]);

  return (
    <InRestaurantRedemptionContext.Provider
      value={{
        inRestaurantLoyaltyEnabledAtRestaurant,
        generateShortCode,
        inRestaurantRedemptionCart,
        addInRestaurantRedemptionEntry,
        removeInRestaurantRedemptionEntry,
        updateInRestaurantRedemptionEntryQuantity,
        findInRestaurantCartEntryByIncentive,
        isInRestaurantRedemptionCartEmpty,
        inRestaurantRedemptionEnabled,
        resetInRestaurantRedemption,
        shortCodeLoading,
        shortCodeState,
        shortCode,
        existEntryTypeInRestaurantRedemptionCart,
        clearInRestaurantRedemptionAllRewards,
      }}
    >
      {children}
    </InRestaurantRedemptionContext.Provider>
  );
};
